import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import {
  Button,
  Chip,
  CircularProgress,
  Link,
  makeStyles,
} from "@material-ui/core";
import {
  Chat as ChatIcon,
  Visibility as VisibilityIcon,
  OpenInNew as OpenInNewIcon,
} from "@material-ui/icons";

import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { fmtDate, fmtTime } from "../../translate/localeHelper";

const useStyles = makeStyles((theme) => ({
  card: {
    alignSelf: "center",
    maxWidth: 520,
    width: "92%",
    margin: "8px 0",
    padding: 14,
    borderRadius: 10,
    background: theme.palette.type === "dark" ? "#1b2430" : "#ffffff",
    border: `1px solid ${theme.palette.type === "dark" ? "#2c3b4d" : "#e0e0e0"}`,
    boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
    fontSize: 14,
  },
  header: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 8,
  },
  title: {
    fontWeight: 600,
    fontSize: 15,
    marginRight: 4,
  },
  badge: {
    height: 20,
    fontSize: 11,
    fontWeight: 700,
  },
  badgeExact: { background: "#00c853", color: "#fff" },
  badgeSimilar: { background: "#ff8f00", color: "#fff" },
  badgeTest: { background: "#9e9e9e", color: "#fff" },
  requestBlock: {
    padding: "8px 10px",
    borderRadius: 8,
    background: theme.palette.type === "dark" ? "#22303f" : "#f5f7fa",
    marginBottom: 8,
  },
  requestText: {
    fontStyle: "italic",
    marginBottom: 6,
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
  },
  chip: {
    height: 22,
    fontSize: 12,
  },
  meta: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginBottom: 8,
  },
  matchesHeader: {
    fontWeight: 600,
    fontSize: 13,
    margin: "4px 0",
  },
  match: {
    padding: "6px 8px",
    borderRadius: 6,
    marginBottom: 4,
    background: theme.palette.type === "dark" ? "#22303f" : "#f0f3f7",
  },
  matchTitle: {
    fontWeight: 600,
  },
  matchSub: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    alignItems: "center",
  },
  matchLevel: {
    fontSize: 10,
    fontWeight: 700,
    padding: "1px 5px",
    borderRadius: 4,
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 10,
  },
  timestamp: {
    marginLeft: "auto",
    fontSize: 11,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },
}));

const fmtPrice = (m) => {
  if (m.precio == null) return i18n.t("matchpropCard.priceOnRequest");
  return `${m.moneda || ""} ${m.precio}`.trim();
};

const MatchPropAlertCard = ({ payload, createdAt }) => {
  const classes = useStyles();
  const history = useHistory();
  const [loadingAuthor, setLoadingAuthor] = useState(false);
  const [loadingGroup, setLoadingGroup] = useState(false);

  const request = payload.request || {};
  const matches = payload.matches || [];
  const author = payload.author || {};
  const group = payload.group || {};
  const isExact = payload.topLevel === "EXACTO";

  const levelLabel = (nivel) =>
    nivel === "EXACTO"
      ? i18n.t("matchpropCard.levelExact")
      : i18n.t("matchpropCard.levelSimilar");

  const handleContactAuthor = async () => {
    if (!payload.alertId) return;
    setLoadingAuthor(true);
    try {
      const { data } = await api.post(
        `/matchprop/alerts/${payload.alertId}/contact-author`
      );
      if (data?.ticketId) {
        toast.success(i18n.t("matchpropCard.contactOk"));
        history.push(`/tickets/${data.ticketId}`);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingAuthor(false);
    }
  };

  const handleViewGroup = async () => {
    if (!payload.alertId) return;
    setLoadingGroup(true);
    try {
      const { data } = await api.get(
        `/matchprop/alerts/${payload.alertId}/group-ticket`
      );
      if (data?.ticketId) {
        // scroll-to-message is not supported yet — degrade to opening the ticket.
        history.push(`/tickets/${data.ticketId}`);
      } else {
        toast.error(i18n.t("matchpropCard.groupNotFound"));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingGroup(false);
    }
  };

  const authorName = author.name || author.phone || "—";

  return (
    <div className={classes.card}>
      <div className={classes.header}>
        <span role="img" aria-label="bell">🔔</span>
        <span className={classes.title}>{i18n.t("matchpropCard.title")}</span>
        <Chip
          size="small"
          label={isExact ? i18n.t("matchpropCard.levelExact") : i18n.t("matchpropCard.levelSimilar")}
          className={`${classes.badge} ${isExact ? classes.badgeExact : classes.badgeSimilar}`}
        />
        {payload.dryRun && (
          <Chip
            size="small"
            label={i18n.t("matchpropCard.test")}
            className={`${classes.badge} ${classes.badgeTest}`}
          />
        )}
        {createdAt && (
          <span className={classes.timestamp}>
            {fmtDate(createdAt)} · {fmtTime(createdAt)}
          </span>
        )}
      </div>

      <div className={classes.requestBlock}>
        {request.textoOriginal && (
          <div className={classes.requestText}>"{request.textoOriginal}"</div>
        )}
        <div className={classes.chips}>
          {request.operacion && (
            <Chip className={classes.chip} size="small" label={`${i18n.t("matchpropCard.operacion")}: ${request.operacion}`} />
          )}
          {request.tipo && (
            <Chip className={classes.chip} size="small" label={`${i18n.t("matchpropCard.tipo")}: ${request.tipo}`} />
          )}
          {request.dorms != null && (
            <Chip className={classes.chip} size="small" label={`${i18n.t("matchpropCard.dorms")}: ${request.dorms}`} />
          )}
          {Array.isArray(request.zonas) && request.zonas.length > 0 && (
            <Chip className={classes.chip} size="small" label={`${i18n.t("matchpropCard.zonas")}: ${request.zonas.join(", ")}`} />
          )}
          {request.precioMax != null && (
            <Chip className={classes.chip} size="small" label={`${i18n.t("matchpropCard.precioMax")}: ${request.moneda || ""} ${request.precioMax}`} />
          )}
        </div>
      </div>

      <div className={classes.meta}>
        {authorName}
        {author.phone ? ` · ${author.phone}` : ""}
        {group.name ? ` · ${i18n.t("matchpropCard.inGroup", { group: group.name })}` : ""}
      </div>

      {matches.length > 0 && (
        <>
          <div className={classes.matchesHeader}>
            {i18n.t("matchpropCard.matchesHeader")} ({matches.length})
          </div>
          {matches.map((m, i) => (
            <div className={classes.match} key={m.externalId || i}>
              <div className={classes.matchTitle}>{m.titulo || m.externalId}</div>
              <div className={classes.matchSub}>
                <span
                  className={classes.matchLevel}
                  style={{
                    background: m.nivel === "EXACTO" ? "#00c853" : "#ff8f00",
                    color: "#fff",
                  }}
                >
                  {levelLabel(m.nivel)}
                </span>
                {m.zona && <span>{m.zona}</span>}
                {m.dorms != null && <span>{m.dorms} {i18n.t("matchpropCard.dorms")}</span>}
                <span>{fmtPrice(m)}</span>
                {m.url && (
                  <Link href={m.url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                    <OpenInNewIcon style={{ fontSize: 13 }} />
                    {i18n.t("matchpropCard.viewListing")}
                  </Link>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      <div className={classes.actions}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={loadingAuthor ? <CircularProgress size={16} color="inherit" /> : <ChatIcon />}
          disabled={loadingAuthor || !payload.alertId || !author.phone}
          onClick={handleContactAuthor}
        >
          {author.name
            ? i18n.t("matchpropCard.contactAuthor", { name: author.name })
            : i18n.t("matchpropCard.contactAuthorShort")}
        </Button>
        {/* Botón "Ver pedido en el grupo" oculto hasta feat scroll-to-message:
            sin posicionar en el mensaje original no aporta (solo abre el ticket
            del grupo). El endpoint GET group-ticket queda inerte en backend para
            reusarlo cuando exista scroll-to-message. handleViewGroup se conserva. */}
      </div>
    </div>
  );
};

export default MatchPropAlertCard;
